import {useContext, useEffect, useMemo, useState} from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  IconButton,
  Input,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCopy,
  FiPause,
  FiPlay,
  FiPlus,
  FiRefreshCw,
  FiSend,
  FiTrash2,
} from "react-icons/fi";
import {FaEraser, FaPaintBrush} from "react-icons/fa";
import {PixelGrid} from "../components/PixelGrid";
import {AdapterContext, ConnectionContext} from "../Contexts";
import {bitmapByteLength, createBitmap, decodeBitmap, encodeBitmap} from "./images";

const loadFrames = (adapter, profile) => {
  const bytes = bitmapByteLength(profile);
  const saved = localStorage.getItem(`animation-draft:${adapter.id}:${profile.id}`);
  const legacy = profile.id === "c3-48x12" ? localStorage.getItem("animation") : null;
  let values = legacy?.split("\n") || [];
  try {
    if (saved) values = JSON.parse(saved);
  } catch {
    values = [];
  }
  if (!Array.isArray(values)) values = [];
  const frames = values.map((value) => decodeBitmap(value, bytes)).filter(Boolean);
  return frames.length ? frames : [createBitmap(profile)];
};

function AnimationTool({label, icon, active, onClick, isDisabled}) {
  return (
    <Tooltip label={label} hasArrow>
      <IconButton
        aria-label={label}
        icon={icon}
        onClick={onClick}
        isDisabled={isDisabled}
        size="sm"
        borderRadius="4px"
        variant={active ? "solid" : "ghost"}
        colorScheme={active ? "studio" : "gray"}
        color={active ? "gray.900" : "gray.300"}
      />
    </Tooltip>
  );
}

export function PixelAnimations() {
  const adapter = useContext(AdapterContext);
  const connected = useContext(ConnectionContext);
  const toast = useToast();
  const [profile, setProfile] = useState(() => adapter.getDisplayProfile());
  const [frames, setFrames] = useState(() => loadFrames(adapter, profile));
  const [activeIndex, setActiveIndex] = useState(0);
  const [tool, setTool] = useState("draw");
  const [mirror, setMirror] = useState("none");
  const [speed, setSpeed] = useState(130);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [encodedDraft, setEncodedDraft] = useState("");
  const [invalidInput, setInvalidInput] = useState(false);

  const profiles = useMemo(() => adapter.listDisplayProfiles(), [adapter]);
  const activeFrame = frames[Math.min(activeIndex, frames.length - 1)] || createBitmap(profile);

  useEffect(() => {
    const detected = adapter.getDisplayProfile();
    setProfile((current) => current.id === detected.id ? current : detected);
  }, [adapter, connected]);

  useEffect(() => {
    const nextFrames = loadFrames(adapter, profile);
    setFrames(nextFrames);
    setActiveIndex(0);
    setPreviewIndex(0);
    setEncodedDraft(nextFrames.map(encodeBitmap).join("\n"));
    setInvalidInput(false);
  }, [adapter, profile]);

  useEffect(() => {
    const encoded = frames.map(encodeBitmap);
    localStorage.setItem(`animation-draft:${adapter.id}:${profile.id}`, JSON.stringify(encoded));
    setEncodedDraft(encoded.join("\n"));
  }, [adapter.id, frames, profile.id]);

  useEffect(() => {
    if (!previewing || frames.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setPreviewIndex((index) => (index + 1) % frames.length);
    }, Math.max(40, speed * 2));
    return () => window.clearInterval(timer);
  }, [frames.length, previewing, speed]);

  const updateFrame = (bitmap) => {
    setFrames((items) => items.map((item, index) => (
      index === activeIndex ? Uint8Array.from(bitmap) : item
    )));
  };

  const addFrame = (copyCurrent = false) => {
    if (frames.length >= 255) return;
    const next = copyCurrent ? Uint8Array.from(activeFrame) : createBitmap(profile);
    setFrames((items) => [
      ...items.slice(0, activeIndex + 1),
      next,
      ...items.slice(activeIndex + 1),
    ]);
    setActiveIndex((index) => index + 1);
  };

  const deleteFrame = () => {
    if (frames.length === 1) {
      updateFrame(createBitmap(profile));
      return;
    }
    setFrames((items) => items.filter((_, index) => index !== activeIndex));
    setActiveIndex((index) => Math.max(0, Math.min(index, frames.length - 2)));
  };

  const moveFrame = (direction) => {
    const target = activeIndex + direction;
    if (target < 0 || target >= frames.length) return;
    setFrames((items) => {
      const next = [...items];
      [next[activeIndex], next[target]] = [next[target], next[activeIndex]];
      return next;
    });
    setActiveIndex(target);
  };

  const send = async () => {
    setSending(true);
    try {
      await adapter.playAnimationFrames(frames, {speed});
      toast({title: `${frames.length} frames sent`, status: "success", duration: 1800});
    } catch (error) {
      toast({title: "Animation failed", description: error.message, status: "error", duration: 4000});
    } finally {
      setSending(false);
    }
  };

  return (
    <Grid templateColumns={{base: "minmax(0, 1fr)", xl: "minmax(0, 1fr) 264px"}} gap={{base: 6, xl: 8}}>
      <Box minW={0}>
        <Flex align="center" justify="space-between" gap={3} mb={4} wrap="wrap">
          <HStack spacing={1} p="3px" bg="#111315" border="1px solid" borderColor="#2b2f34" borderRadius="5px">
            <AnimationTool label="Brush" icon={<FaPaintBrush />} active={tool === "draw"} onClick={() => setTool("draw")} />
            <AnimationTool label="Eraser" icon={<FaEraser />} active={tool === "erase"} onClick={() => setTool("erase")} />
            <Divider orientation="vertical" h="22px" borderColor="whiteAlpha.200" />
            <AnimationTool label="No mirror" icon={<FiRefreshCw />} active={mirror === "none"} onClick={() => setMirror("none")} />
            <AnimationTool label="Left/right mirror" icon={<FiArrowLeft />} active={mirror === "horizontal"} onClick={() => setMirror("horizontal")} />
            <AnimationTool label="Up/down mirror" icon={<FiArrowRight />} active={mirror === "vertical"} onClick={() => setMirror("vertical")} />
            <AnimationTool label="Four-way mirror" icon={<FiRefreshCw />} active={mirror === "quad"} onClick={() => setMirror("quad")} />
          </HStack>
          <HStack spacing={1}>
            <AnimationTool label="Clear frame" icon={<FiTrash2 />} onClick={() => updateFrame(createBitmap(profile))} />
          </HStack>
        </Flex>

        <Flex
          minH={{base: "250px", md: profile.height > 12 ? "520px" : "340px"}}
          align="center"
          justify="center"
          bg="#0e1012"
          border="1px solid"
          borderColor="#292d31"
          borderRadius="5px"
          p={{base: 4, md: 10}}
          overflowX="auto"
        >
          <PixelGrid
            bitmap={previewing ? frames[previewIndex] : activeFrame}
            profile={profile}
            tool={tool}
            mirror={mirror}
            onChange={updateFrame}
            interactive={!previewing}
          />
        </Flex>

        <Flex mt={4} align="center" justify="space-between" gap={3} wrap="wrap">
          <HStack spacing={2}>
            <AnimationTool
              label={previewing ? "Stop preview" : "Preview animation"}
              icon={previewing ? <FiPause /> : <FiPlay />}
              active={previewing}
              onClick={() => {
                setPreviewIndex(0);
                setPreviewing((value) => !value);
              }}
              isDisabled={frames.length < 2}
            />
            <Text color="#7d838c" fontSize="11px" fontFamily="mono">Frame {activeIndex + 1} / {frames.length}</Text>
          </HStack>
          <Button
            leftIcon={<FiSend />}
            colorScheme="studio"
            color="#17130a"
            borderRadius="5px"
            onClick={send}
            isLoading={sending}
            isDisabled={!connected || !frames.length}
          >
            Send animation
          </Button>
        </Flex>

        <Box mt={5} overflowX="auto" pb={2}>
          <HStack spacing={2} align="stretch" minW="max-content">
            {frames.map((frame, index) => (
              <Box
                as="button"
                type="button"
                key={index}
                onClick={() => {
                  setPreviewing(false);
                  setActiveIndex(index);
                }}
                width={profile.height > 12 ? "84px" : "132px"}
                p="5px"
                border="1px solid"
                borderColor={index === activeIndex ? "#ff7a68" : "#292d31"}
                borderRadius="5px"
                bg={index === activeIndex ? "rgba(255,122,104,.07)" : "transparent"}
                textAlign="left"
              >
                <PixelGrid bitmap={frame} profile={profile} onChange={() => {}} interactive={false} />
                <Text mt={1} color="gray.500" fontSize="10px">{String(index + 1).padStart(2, "0")}</Text>
              </Box>
            ))}
            <Tooltip label="Add frame" hasArrow>
              <IconButton
                aria-label="Add frame"
                icon={<FiPlus />}
                onClick={() => addFrame(false)}
                isDisabled={frames.length >= 255}
                minW="52px"
                h="100%"
                alignSelf="stretch"
                variant="outline"
                borderStyle="dashed"
                borderRadius="5px"
              />
            </Tooltip>
          </HStack>
        </Box>
      </Box>

      <Stack spacing={5} borderTop={{base: "1px solid", xl: 0}} borderLeft={{base: 0, xl: "1px solid"}} borderColor="#292d31" pt={{base: 5, xl: 0}} pl={{base: 0, xl: 6}}>
        <Box>
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={3}>
            Timeline
          </Text>
          <HStack spacing={1}>
            <AnimationTool label="Move frame left" icon={<FiArrowLeft />} onClick={() => moveFrame(-1)} isDisabled={activeIndex === 0} />
            <AnimationTool label="Move frame right" icon={<FiArrowRight />} onClick={() => moveFrame(1)} isDisabled={activeIndex === frames.length - 1} />
            <AnimationTool label="Copy frame" icon={<FiCopy />} onClick={() => addFrame(true)} isDisabled={frames.length >= 255} />
            <AnimationTool label="Delete frame" icon={<FiTrash2 />} onClick={deleteFrame} />
          </HStack>
        </Box>

        <Divider borderColor="#292d31" />

        <FormControl>
          <FormLabel fontSize="sm" color="gray.400">Frame speed</FormLabel>
          <HStack>
            <Slider min={1} max={255} value={speed} onChange={setSpeed} focusThumbOnChange={false}>
              <SliderTrack bg="#272b2f"><SliderFilledTrack bg="#ff7a68" /></SliderTrack>
              <SliderThumb boxSize={4} />
            </Slider>
            <Input
              type="number"
              value={speed}
              min={1}
              max={255}
              onChange={(event) => setSpeed(Math.max(1, Math.min(255, Number(event.target.value))))}
              width="72px"
              size="sm"
              borderRadius="5px"
            />
          </HStack>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color="gray.400">Display profile</FormLabel>
          <Select
            value={profile.id}
            onChange={(event) => {
              const next = adapter.setDisplayProfile(event.target.value);
              setProfile(next);
            }}
            isDisabled={connected}
            size="sm"
            borderRadius="5px"
          >
            {profiles.map((item) => (
              <option key={item.id} value={item.id}>{item.name} - {item.width} x {item.height}</option>
            ))}
          </Select>
        </FormControl>

        <Flex justify="space-between" fontSize="sm">
          <Text color="gray.400">Upload</Text>
          <Badge colorScheme={frames.length === 255 ? "orange" : "gray"}>{frames.length} / 255 frames</Badge>
        </Flex>

        <Divider borderColor="#292d31" />

        <Accordion allowToggle>
          <AccordionItem border="0">
            <AccordionButton px={0} _hover={{bg: "transparent"}}>
              <Box as="span" flex="1" textAlign="left" fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
                Encoded frames
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={0}>
              <Textarea
                value={encodedDraft}
                isInvalid={invalidInput}
                fontFamily="mono"
                fontSize="xs"
                onChange={(event) => {
                  const value = event.target.value;
                  setEncodedDraft(value);
                  const bytes = bitmapByteLength(profile);
                  const decoded = value.split("\n").filter(Boolean).map((item) => decodeBitmap(item, bytes));
                  const invalid = !decoded.length || decoded.some((item) => !item) || decoded.length > 255;
                  setInvalidInput(invalid);
                  if (!invalid) {
                    setFrames(decoded);
                    setActiveIndex(0);
                  }
                }}
                borderRadius="5px"
                minH="110px"
                resize="vertical"
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Stack>
    </Grid>
  );
}
